import { PageConfigField } from "../../config/pageConfig.types";
import { fullWidth, halfWidth, thirdWidth } from "../../blocks/layout/columnSpans";
import { enumToOptions } from "../../blocks/fields/enumToOptions";
import {
  AutomationScheduleRelativeDirection,
  AutomationTriggerType,
} from "../../config/enums";
import { Time, When } from "./triggerTiming";

const triggerTypeOptions = [
  {
    label: "Abandoned Basket",
    value: AutomationTriggerType["Abandoned Basket"],
    description:
      "Send the selected communication when a supported abandoned basket event is received.",
  },
];

/**
 * What an automation delivers and when: the Delivery wizard step on a new automation, and
 * the form until it is published, when the Delivery card shows them instead.
 */
export const automationDeliveryFields: PageConfigField[] = [
  {
    id: "field-triggertype",
    type: "radio",
    name: "triggertype",
    label: "Trigger",
    cards: true,
    options: triggerTypeOptions,
    helpText: "Choose the event that should start this automation.",
    visibleWhen: "automationIsTriggered",
    span: fullWidth,
  },
  {
    id: "field-triggerwhen",
    type: "picklist",
    name: "triggerwhen",
    label: "When",
    required: true,
    options: [{ Key: When.After, Value: "After" }],
    visibleWhen: "automationIsTriggered",
    span: thirdWidth,
  },
  {
    id: "field-triggerinterval",
    type: "number",
    name: "triggerinterval",
    label: "Time",
    required: true,
    validatorKey: "automationTriggerInterval",
    visibleWhen: "automationIsTriggered",
    span: thirdWidth,
  },
  {
    id: "field-triggertimeunit",
    type: "picklist",
    name: "triggertimeunit",
    label: "Unit",
    required: true,
    options: enumToOptions(Time),
    visibleWhen: "automationIsTriggered",
    span: thirdWidth,
  },
  {
    id: "field-communicationid",
    type: "lookup",
    name: "communicationid",
    label: "Communication",
    lookupEntity: "g4c_communication",
    searchFilter: "contactflow",
    searchable: true,
    required: true,
    validatorKey: "automationRequiredLookup",
    span: halfWidth,
  },
  {
    id: "field-canbecancelled",
    type: "boolean",
    name: "canbecancelled",
    label: "Can Be Cancelled",
    layout: "stacked",
    helpText:
      "Cancel this automation send if the purchaser completes an order before it is due to send.",
    visibleWhen: "automationIsTriggered",
    span: halfWidth,
  },
  {
    id: "field-audienceid",
    type: "lookup",
    name: "audienceid",
    label: "Audience",
    lookupEntity: "g4c_communicationaudience",
    searchable: true,
    required: true,
    validatorKey: "automationRequiredLookup",
    visibleWhen: "automationHasScheduleMode",
    span: halfWidth,
  },
  {
    id: "field-scheduledat",
    type: "time",
    name: "scheduledat",
    label: "Send Time",
    required: true,
    visibleWhen: "automationIsDaily",
    span: halfWidth,
  },
  {
    id: "field-sendwindowstart",
    type: "time",
    name: "sendwindowstart",
    label: "Send From",
    validatorKey: "automationSendWindowStart",
    helpText:
      "Earliest time of day to send. Leave both times blank to send at any time.",
    visibleWhen: "automationIsAbandonedBasket",
    span: halfWidth,
  },
  {
    id: "field-sendwindowend",
    type: "time",
    name: "sendwindowend",
    label: "Send Until",
    validatorKey: "automationSendWindowEnd",
    helpText:
      "Time of day sending stops. Later emails wait until the next Send From.",
    visibleWhen: "automationIsAbandonedBasket",
    span: halfWidth,
  },
  {
    id: "content-fixture-notice",
    kind: "content",
    type: "text",
    content:
      "Fixture-relative schedules depend on fixture metadata being tagged correctly and kept up to date.",
    visibleWhen: "automationIsFixture",
    span: fullWidth,
  },
  {
    id: "field-competitionid",
    type: "lookup",
    name: "competitionid",
    label: "Competition",
    lookupEntity: "dwcompetition",
    searchable: true,
    required: true,
    validatorKey: "automationRequiredLookup",
    visibleWhen: "automationIsFixture",
    span: halfWidth,
  },
  {
    id: "field-teamid",
    type: "lookup",
    name: "teamid",
    label: "Team",
    lookupEntity: "dwteam",
    searchable: true,
    required: true,
    validatorKey: "automationRequiredLookup",
    visibleWhen: "automationIsFixture",
    span: halfWidth,
  },
  {
    id: "field-fixturerelativedirection",
    type: "picklist",
    name: "fixturerelativedirection",
    label: "Fixture Timing",
    required: true,
    options: enumToOptions(AutomationScheduleRelativeDirection),
    onChange: {
      behaviour: "automationClearOffsetForSameDay",
      target: "fixturerelativeoffset",
    },
    visibleWhen: "automationIsFixture",
    span: thirdWidth,
  },
  {
    id: "field-fixturerelativeoffset",
    type: "number",
    name: "fixturerelativeoffset",
    label: "Fixture Offset (Days)",
    required: true,
    validatorKey: "automationFixtureOffset",
    helpText: "Number of days before or after the fixture.",
    visibleWhen: "automationNeedsFixtureOffset",
    span: thirdWidth,
  },
  {
    id: "field-fixturerelativetime",
    type: "time",
    name: "fixturerelativetime",
    label: "Send Time",
    required: true,
    visibleWhen: "automationHasFixtureDirection",
    span: thirdWidth,
  },
];
